// ============================================================
// STORY MODE — localStorage progress
// ============================================================
//
// Persists chapter completion state. Schema is versioned so future
// fields (best clear time, score, branch choices) can be added
// without breaking existing saves.
//
// Storage key: `pt.story.progress` (single JSON blob).
// ============================================================

const STORAGE_KEY = 'pt.story.progress';
const SCHEMA_VERSION = 1;

interface StoryProgressData {
  version: number;
  /** Map of chapterId → completion timestamp (Unix ms). */
  completed: Record<string, number>;
}

function readBlob(): StoryProgressData {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { version: SCHEMA_VERSION, completed: {} };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: SCHEMA_VERSION, completed: {} };
    const parsed = JSON.parse(raw) as Partial<StoryProgressData>;
    if (parsed.version !== SCHEMA_VERSION || !parsed.completed) {
      return { version: SCHEMA_VERSION, completed: {} };
    }
    return parsed as StoryProgressData;
  } catch {
    return { version: SCHEMA_VERSION, completed: {} };
  }
}

function writeBlob(data: StoryProgressData): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[StoryProgress] persist failed', e);
  }
}

/** True if the chapter has been cleared at least once. */
export function isCompleted(chapterId: string): boolean {
  return readBlob().completed[chapterId] != null;
}

/** Mark chapter cleared. Idempotent — overwrites timestamp on re-clear. */
export function markCompleted(chapterId: string): void {
  const data = readBlob();
  data.completed[chapterId] = Date.now();
  writeBlob(data);
}

/** Wipe all progress (debug / settings). */
export function reset(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try { window.localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
}

/** Snapshot of all completed chapter IDs. */
export function listCompleted(): string[] {
  return Object.keys(readBlob().completed);
}
